/**
 * Tiptap body utilities for CIH detail pages.
 *
 * Extracts an ordered table of contents (H2 anchors) from a Tiptap doc and
 * returns a normalized doc with stable `id` attrs on every heading so the
 * tab nav anchors land correctly. Also demotes any H1 in the body to H2 —
 * the page H1 belongs to the hero, so a body H1 would create a duplicate
 * H1 (bad for both SEO and accessibility).
 *
 * Aliases: body toc, framework toc, heading anchors, tiptap headings, h2 nav
 */
import type { JsonValue } from "~/lib/shared/json";
import { generateSlug } from "~/lib/shared/slug";

export type BodyHeading = {
  /** DOM id assigned to the heading. Unique within the doc. */
  readonly id: string;
  /** Heading text, plain. */
  readonly label: string;
  /** Heading level after demotion (H1 → H2). Always 2 or 3. */
  readonly level: 2 | 3;
};

type TiptapNode = {
  type?: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  text?: string;
};

/** Concatenate all text nodes inside a heading. */
function headingText(node: TiptapNode): string {
  if (!Array.isArray(node.content)) return "";
  let out = "";
  for (const child of node.content) {
    if (typeof child.text === "string") out += child.text;
    else if (Array.isArray(child.content)) out += headingText(child);
  }
  return out.trim();
}

/**
 * Parse a body value (Tiptap JSON object, JSON string, or null) and return
 * the canonical doc node. Returns null when the input isn't a recognizable
 * Tiptap doc.
 */
function parseTiptapDoc(body: unknown): TiptapNode | null {
  if (body == null) return null;
  let doc: unknown = body;
  if (typeof body === "string") {
    try {
      doc = JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (
    typeof doc !== "object" ||
    doc === null ||
    Array.isArray(doc) ||
    (doc as TiptapNode).type !== "doc"
  ) {
    return null;
  }
  return doc as TiptapNode;
}

/**
 * Walk a Tiptap doc, returning a clone with each H2/H3 carrying a unique
 * `id` attr derived from its text content, and any H1 demoted to H2. The
 * returned `headings` list is in document order — feed it straight into
 * the tab nav.
 *
 * Heading IDs are stable for the same input doc (deterministic slugify +
 * `-2`, `-3`, ... suffixes for collisions). The `untitled-section`
 * fallback is used for empty-text headings.
 */
export function annotateBodyHeadings(body: unknown): {
  doc: JsonValue | null;
  headings: BodyHeading[];
} {
  const doc = parseTiptapDoc(body);
  if (!doc) {
    return {
      doc: (body ?? null) as JsonValue | null,
      headings: [],
    };
  }

  const used = new Map<string, number>();
  const headings: BodyHeading[] = [];

  function uniqueId(base: string): string {
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    return seen === 0 ? base : `${base}-${seen + 1}`;
  }

  function walk(node: TiptapNode): TiptapNode {
    const next: TiptapNode = { ...node };
    if (Array.isArray(node.content)) {
      next.content = node.content.map(walk);
    }
    if (next.type !== "heading") return next;

    const rawLevel = Number(next.attrs?.level);
    // Demote H1 → H2 so the hero stays the only H1 on the page. Anything
    // deeper than H3 isn't surfaced in the section nav (still rendered).
    const level: 2 | 3 = rawLevel <= 2 ? 2 : 3;
    const text = headingText(next);
    const slugBase = generateSlug(text).slice(0, 80) || "untitled-section";
    const id = uniqueId(slugBase);

    next.attrs = { ...(next.attrs ?? {}), level, id };
    if (level === 2) {
      headings.push({ id, label: text || "Untitled section", level });
    }
    return next;
  }

  return { doc: walk(doc) as unknown as JsonValue, headings };
}

/**
 * Headings a CIH detail page already prints above the body. A body that opens
 * by restating one repeats the page's own furniture: the reader meets the
 * "About" header and then, instead of the description it promises, a second
 * heading saying the same thing.
 */
const RESTATED_OPENING_HEADINGS: ReadonlySet<string> = new Set([
  "overview",
  "about",
]);

/**
 * Return a body without an opening heading that only restates the section
 * header rendered directly above it.
 *
 * Only the doc's first node is considered. A heading further down names a
 * section of the body's own and stays where its author put it.
 *
 * Run this before `annotateBodyHeadings` so a dropped heading never reaches
 * the section nav either — that is what put two identical "Overview" pills in
 * the tab bar. A body that isn't a recognizable Tiptap doc is returned as-is.
 *
 * Aliases: drop leading heading, redundant overview heading, restated heading
 */
export function dropRestatedOpeningHeading(body: unknown): JsonValue | null {
  const doc = parseTiptapDoc(body);
  const unchanged = (body ?? null) as JsonValue | null;
  if (!doc || !Array.isArray(doc.content)) return unchanged;

  const [first, ...rest] = doc.content;
  if (first === undefined || first.type !== "heading") return unchanged;
  if (!RESTATED_OPENING_HEADINGS.has(headingText(first).toLowerCase())) {
    return unchanged;
  }

  return { ...doc, content: rest } as unknown as JsonValue;
}
